import { Mesh, VideoTexture, SRGBColorSpace, LinearFilter } from 'three/webgpu'
import { types } from '@theatre/core'
import ScreenMaterial from '../materials/ScreenMaterial'

export default class DeviceScreen extends Mesh {
	static defaults = {
		source: null,
		video: null,
		material: {},
	}

	constructor(options = {}) {
		const { source, video, material } = { ...DeviceScreen.defaults, ...options }

		super(source.geometry, new ScreenMaterial(DeviceScreen.texture(video), { geometry: source.geometry, ...material }))

		this.video = video
		this.source = source

		this.position.copy(source.position)
		this.quaternion.copy(source.quaternion)
		this.scale.copy(source.scale)

		source.visible = false
		source.parent.add(this)

		this._onGesture = () => this.play()

		this.play()
		document.addEventListener('pointerdown', this._onGesture, { once: true })
	}

	static texture(video) {
		const texture = new VideoTexture(video)

		texture.colorSpace = SRGBColorSpace
		texture.minFilter = texture.magFilter = LinearFilter
		texture.generateMipmaps = false
		texture.flipY = false

		return texture
	}

	set interference(value) {
		this.material.interference.value = value
	}

	update(delta, elapsed) {
		this.material.time.value = elapsed
	}

	play() {
		this.video.play().catch(() => {})
	}

	bindStudio(sheet) {
		if (!sheet) return

		const { resolution, pitch, gap, strength, bleed, brightness, scale, offset } = this.material

		this.controls = sheet.object('Device / Screen', {
			resolution: types.compound({
				x: types.number(resolution.value.x, { range: [4, 512], nudgeMultiplier: 1 }),
				y: types.number(resolution.value.y, { range: [4, 512], nudgeMultiplier: 1 }),
			}),
			pitch: types.compound({
				x: types.number(pitch.value.x, { range: [4, 256], nudgeMultiplier: 1 }),
				y: types.number(pitch.value.y, { range: [4, 256], nudgeMultiplier: 1 }),
			}),
			scale: types.compound({
				x: types.number(scale.value.x, { range: [-2, 2] }),
				y: types.number(scale.value.y, { range: [-2, 2] }),
			}),
			offset: types.compound({
				x: types.number(offset.value.x, { range: [-1, 2] }),
				y: types.number(offset.value.y, { range: [-1, 2] }),
			}),
			gap: types.number(gap.value, { range: [0, 0.9] }),
			strength: types.number(strength.value, { range: [0, 1] }),
			bleed: types.number(bleed.value, { range: [0, 1] }),
			brightness: types.number(brightness.value, { range: [0, 5] }),
		})

		return this.controls.onValuesChange(values => {
			resolution.value.set(values.resolution.x, values.resolution.y)
			pitch.value.set(values.pitch.x, values.pitch.y)
			scale.value.set(values.scale.x, values.scale.y)
			offset.value.set(values.offset.x, values.offset.y)
			gap.value = values.gap
			strength.value = values.strength
			bleed.value = values.bleed
			brightness.value = values.brightness
		})
	}

	dispose() {
		document.removeEventListener('pointerdown', this._onGesture)
		this.video.pause()
		this.material.map.dispose()
		this.material.dispose()
		this.source.visible = true
		this.removeFromParent()
	}
}
